import { getSelectorForElement, findElementBySelector } from './selectorUtils';

const DEFAULT_SYNC_URL = 'ws://localhost:3200';

export default function createSyncService(mode) {
    let ws = null;
    let role = mode;
    let isApplyingRemote = false;
    let clickCaptureHandler = null;

    function getSyncUrl() {
        return process.env.REACT_APP_SYNC_URL || DEFAULT_SYNC_URL;
    }

    function connect() {
        if (role !== 'master' && role !== 'servant') return;
        
        const url = `${getSyncUrl()}?role=${encodeURIComponent(role)}`;
        ws = new WebSocket(url);

        ws.addEventListener('open', () => {
            if (role === 'master') {
                const hash = window.location.hash || '#/';
                send({ type: 'init', url: hash });
                startClickCapture();
            } else {
                startClickReplay();
            }
        });

        ws.addEventListener('message', (ev) => {
            try {
                const msg = JSON.parse(ev.data || '{}');
                if (role === 'servant') {
                    handleServantMessage(msg);
                }
            } catch (err) {
                console.error('[sync] parse error:', err);
            }
        });

        ws.addEventListener('close', () => {
            stopClickCapture();
            ws = null;
        });

        ws.addEventListener('error', (err) => {
            console.error('[sync] error:', err);
        });
    }

    function send(msg) {
        if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify(msg));
        }
    }

    function handleServantMessage(msg) {
        if (msg.type === 'init' && typeof msg.url === 'string') {
            isApplyingRemote = true;
            window.location.hash = msg.url.startsWith('#') ? msg.url : '#' + msg.url;
            setTimeout(() => { isApplyingRemote = false; }, 100);
        } else if (msg.type === 'click' && msg.selector) {
            isApplyingRemote = true;
            const el = findElementBySelector(msg.selector);
            if (el) {
                if (typeof el.click === 'function') {
                    el.click();
                } else {
                    el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
                }
            }
            setTimeout(() => { isApplyingRemote = false; }, 50);
        } else if (msg.type === 'video' && msg.selector) {
            isApplyingRemote = true;
            const video = findElementBySelector(msg.selector);
            if (video && video.tagName === 'VIDEO') {
                if (msg.action === 'play') {
                    video.play().catch(() => {});
                } else if (msg.action === 'pause') {
                    video.pause();
                } else if (msg.action === 'seek' && typeof msg.currentTime === 'number') {
                    video.currentTime = msg.currentTime;
                } else if (msg.action === 'volume' && typeof msg.volume === 'number') {
                    video.volume = Math.max(0, Math.min(1, msg.volume));
                } else if (msg.action === 'muted' && typeof msg.muted === 'boolean') {
                    video.muted = msg.muted;
                }
            }
            setTimeout(() => { isApplyingRemote = false; }, 50);
        }
    }

    function getClickTarget(el) {
        const interactive = el.closest('a, button, [role="button"], [role="tab"], [role="link"], input[type="submit"], input[type="button"], [onclick]');
        return interactive || el;
    }

    function startClickCapture() {
        if (clickCaptureHandler) return;
        clickCaptureHandler = (e) => {
            if (isApplyingRemote || !ws || ws.readyState !== WebSocket.OPEN) return;
            const target = getClickTarget(e.target);
            const selector = getSelectorForElement(target);
            console.log('[sync] target:', target);
            console.log('[sync] selector:', selector); 
            if (selector) {
                send({ type: 'click', selector });
            }
        };
        document.addEventListener('click', clickCaptureHandler, true);
    }

    function stopClickCapture() {
        if (clickCaptureHandler) {
            document.removeEventListener('click', clickCaptureHandler, true);
            clickCaptureHandler = null;
        }
    }

    function startClickReplay() {
        // Servant listens via handleServantMessage - no document listener needed
    }

    function disconnect() {
        stopClickCapture();
        if (ws) {
            ws.close();
            ws = null;
        }
    }

    return {
        connect,
        disconnect,
        send,
    };
}
